import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Space, Tag, Input, Select, Typography, Popconfirm,
  message, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, AlertOutlined, ReloadOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { newsApi, NewsItem } from '../../api/news';
import { NEWS_CATEGORIES, getCategoryLabel } from '../../utils/constants';
import { PageHeader, StatCard, StatRow } from '../../components/shared';
import dayjs from 'dayjs';

const { Text } = Typography;

const NewsList: React.FC = () => {
  const [data, setData] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | undefined>();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await newsApi.list({ page, size: 20, search: search || undefined, category });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      message.error('Ошибка загрузки новостей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [page, category]);

  const handleSearch = () => { setPage(1); fetchData(); };

  const handleDelete = async (id: number) => {
    try {
      await newsApi.delete(id);
      message.success('Новость удалена');
      fetchData();
    } catch {
      message.error('Ошибка удаления');
    }
  };

  const emergencyCount = data.filter((n) => n.is_emergency).length;

  const columns = [
    {
      title: 'Заголовок',
      dataIndex: 'title_ru',
      key: 'title',
      render: (text: string, record: NewsItem) => (
        <Space direction="vertical" size={0}>
          <Space>
            {record.is_emergency && (
              <Tag color="red">срочно</Tag>
            )}
            <Text strong style={{ fontSize: 14 }}>{text}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.title_kk}</Text>
        </Space>
      ),
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (cat: string) => (
        <Tag>
          {getCategoryLabel(cat)}
        </Tag>
      ),
    },
    {
      title: 'Дата',
      dataIndex: 'published_at',
      key: 'date',
      width: 140,
      render: (d: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {dayjs(d).format('DD.MM.YYYY HH:mm')}
        </Text>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_: any, record: NewsItem) => (
        <Space>
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/news/${record.id}/edit`)}
            />
          </Tooltip>
          <Popconfirm
            title="Удалить новость?"
            description="Это действие необратимо"
            onConfirm={() => handleDelete(record.id)}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Удалить">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Новости"
        subtitle="Управление новостной лентой приложения"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
           
            onClick={() => navigate('/news/create')}
            style={{ borderRadius: 12, fontWeight: 600 }}
          >
            Создать новость
          </Button>
        }
      />

      <StatRow>
        <StatCard label="Всего" value={total} icon={<FileTextOutlined />} />
        <StatCard
          label="Экстренных"
          value={emergencyCount}
          icon={<AlertOutlined />}
          color="var(--l-danger)"
          colorValue
        />
      </StatRow>

      {/* Filters */}
      <Card style={{ borderRadius: 16, marginBottom: 16 }} bodyStyle={{ padding: '16px 20px' }}>
        <Space size={12} wrap>
          <Input
            placeholder="Поиск по заголовку..."
            prefix={<SearchOutlined style={{ color: 'var(--l-faint)' }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 280, borderRadius: 10 }}
            allowClear
          />
          <Select
            placeholder="Категория"
            allowClear
            value={category}
            onChange={(v) => { setCategory(v); setPage(1); }}
            style={{ width: 160 }}
            options={NEWS_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Обновить
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            onChange: setPage,
            showTotal: (t) => `Всего: ${t}`,
            showSizeChanger: false,
          }}
          onRow={(record) => ({
            style: { cursor: 'pointer' },
            onClick: () => navigate(`/news/${record.id}/edit`),
          })}
        />
      </Card>
    </div>
  );
};



export default NewsList;
